import React, { useEffect, useMemo, useState } from 'react';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useLoomVideoEditorInserter } from '@atlassian/jira-loom-utils/src/controllers/use-loom-video-editor-inserter/index.tsx';
import {
	componentWithCondition,
	functionWithCondition,
} from '@atlassian/jira-feature-flagging-utils';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { MentionsContainer } from '@atlassian/jira-editor-wrapper/src/mention-reccommendations/index.tsx';
import { DraftReplyPillContainer } from '@atlassian/jira-editor-wrapper/src/draft-reply-pills/index.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useIssueTypeField } from '@atlassian/jira-issue-field-issue-type/src/services/index.tsx';
import {
	SOFTWARE_PROJECT,
	SERVICE_DESK_PROJECT,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/index.tsx';
import {
	useIsAiEnabledForIssue,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIsIssueOfIncidentsPractice } from '@atlassian/jira-issue-field-servicedesk-practices/src/services/use-practices-field-value/index.tsx';
import { useIsAgentOrProjectAdmin } from '@atlassian/jira-servicedesk-customer-service-issue-view-panel/src/common/utils.tsx';
import { messages as loomBannerMessages } from '../loom-videos/messages.tsx';

import type { OwnProps } from './index.tsx';
import { issueEditorEntrypoint } from './entrypoint.tsx';
import { useIssueLinkSuggestions as useIssueLinkSuggestionsBase } from './issue-link-suggestions-store/index.tsx';

type EditorMentionsAPI = PublicPluginAPI<[MentionsPlugin]>;

const entryPointParams = {};

// This hook is only required for feature gating purposes as we can't condtionally render hooks
const useIssueLinkSuggestionsTemp = () => {
	return [];
};

const useIssueLinkSuggestions = functionWithCondition(
	() => fg('issue-link-suggestions-in-comments'),
	useIssueLinkSuggestionsBase,
	useIssueLinkSuggestionsTemp,
);

const IssueEditor = (props: OwnProps) => {
	const [editorMentionsAPI, setEditorMentionsAPI] = useState<EditorMentionsAPI | undefined>(
		undefined,
	);
	const issueLinkSuggestions = useIssueLinkSuggestions({ existingIssueLinks: props.issueLinks });

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const [{ value: issueType }] = useIssueTypeField({ issueKey });
	const { formatMessage } = useIntl();
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isIssueOfIncidentPractice = useIsIssueOfIncidentsPractice();
	const isAgentOrProjectAdmin = useIsAgentOrProjectAdmin();

	const isPromptLinkedIssuesExperimentEligible = useMemo(() => {
		return (
			(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
			fg('issue-link-suggestions-in-comments')
		);
	}, [projectType]);

	const isMentionExperimentEligible = useMemo(() => {
		return (
			projectType === SOFTWARE_PROJECT &&
			issueType?.name !== formatMessage(loomBannerMessages.bugIssueTypeName)
		);
	}, [projectType, issueType, formatMessage]);

	// Draft Reply Pill is only for JSM product
	const isDraftReplyExperimentEligible = useMemo(() => {
		return (
			projectType === SERVICE_DESK_PROJECT &&
			isAiOptInEnabled &&
			!isIssueOfIncidentPractice &&
			(!fg('draft_reply_agent_check') || isAgentOrProjectAdmin)
		);
	}, [projectType, isAiOptInEnabled, isIssueOfIncidentPractice, isAgentOrProjectAdmin]);

	const contentComponents = useMemo(() => {
		if (
			isPromptLinkedIssuesExperimentEligible &&
			issueLinkSuggestions.length > 0 &&
			expVal('issue-link-suggestions-in-comments-exp', 'isPromptLinkedIssuesEnabled', false)
		) {
			return {
				before: <></>,
				// TODO: Render component from HL-2784
				after: <></>,
			};
		}
		// Placing two separate if statements since we draft reply and mentions mutually exclusive
		if (
			isDraftReplyExperimentEligible &&
			expVal('draft_reply_pre-pill_quality_improve_before_ga', 'ExperimentVersion', 'Control') !==
				'Control'
		) {
			return {
				before: <></>,
				after: <DraftReplyPillContainer />,
			};
		}
		if (
			isMentionExperimentEligible &&
			expVal('recommend_user_mentions_in_comments', 'isMentionsPanelVisible', false)
		) {
			return {
				before: <></>,
				after: <MentionsContainer api={editorMentionsAPI} />,
			};
		}
	}, [
		editorMentionsAPI,
		isDraftReplyExperimentEligible,
		isMentionExperimentEligible,
		isPromptLinkedIssuesExperimentEligible,
		issueLinkSuggestions.length,
	]);

	const runtimeProps = useMemo(() => {
		const shouldPassPromptLinkedIssuesComponent =
			isMentionExperimentEligible &&
			expVal('issue-link-suggestions-in-comments-exp', 'isPromptLinkedIssuesEnabled', false);

		const shouldPassMentionComponent =
			isMentionExperimentEligible &&
			expVal('recommend_user_mentions_in_comments', 'isMentionsPanelVisible', false);

		const shouldPassDraftReplyComponent =
			isDraftReplyExperimentEligible &&
			expVal('draft_reply_pre-pill_quality_improve_before_ga', 'ExperimentVersion', 'Control') !==
				'Control';

		return {
			...props,
			...(props.isAddComment &&
				(shouldPassPromptLinkedIssuesComponent ||
					shouldPassMentionComponent ||
					shouldPassDraftReplyComponent) && { contentComponents }),
			...(props.isAddComment &&
				shouldPassMentionComponent && {
					editorMentionsAPI,
					setEditorMentionsAPI,
				}),
		};
	}, [
		props,
		isDraftReplyExperimentEligible,
		contentComponents,
		editorMentionsAPI,
		setEditorMentionsAPI,
		isMentionExperimentEligible,
	]);

	return <IssueEditorInternal runtimeProps={runtimeProps} />;
};

const IssueEditorWithLoomEmbedder = (props: OwnProps) => {
	const { editorApiRef } = useLoomVideoEditorInserter({
		entryPointLocation: 'jsmIssueView_customerReply_crossflow',
	});

	const [editorMentionsAPI, setEditorMentionsAPI] = useState<EditorMentionsAPI | undefined>(
		undefined,
	);

	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const [{ value: issueType }] = useIssueTypeField({ issueKey });
	const { formatMessage } = useIntl();
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isIssueOfIncidentPractice = useIsIssueOfIncidentsPractice();
	const isAgentOrProjectAdmin = useIsAgentOrProjectAdmin();

	const isMentionExperimentEligible = useMemo(() => {
		return (
			projectType === SOFTWARE_PROJECT &&
			issueType?.name !== formatMessage(loomBannerMessages.bugIssueTypeName)
		);
	}, [projectType, issueType, formatMessage]);

	// Draft reply pill is only for JSM product
	const isDraftReplyExperimentEligible = useMemo(() => {
		return (
			projectType === SERVICE_DESK_PROJECT &&
			isAiOptInEnabled &&
			!isIssueOfIncidentPractice &&
			(!fg('draft_reply_agent_check') || isAgentOrProjectAdmin)
		);
	}, [projectType, isAiOptInEnabled, isIssueOfIncidentPractice, isAgentOrProjectAdmin]);

	const contentComponents = useMemo(() => {
		// Placing two separate if statements since we draft reply and mentions mutually exclusive
		if (
			isDraftReplyExperimentEligible &&
			expVal('draft_reply_pre-pill_quality_improve_before_ga', 'ExperimentVersion', 'Control') !==
				'Control'
		) {
			return {
				before: <></>,
				after: <DraftReplyPillContainer />,
			};
		}
		if (
			isMentionExperimentEligible &&
			expVal('recommend_user_mentions_in_comments', 'isMentionsPanelVisible', false)
		) {
			return {
				before: <></>,
				after: <MentionsContainer api={editorMentionsAPI} />,
			};
		}
	}, [editorMentionsAPI, isDraftReplyExperimentEligible, isMentionExperimentEligible]);

	const runtimeProps = useMemo(() => {
		const shouldPassMentionComponent =
			isMentionExperimentEligible &&
			expVal('recommend_user_mentions_in_comments', 'isMentionsPanelVisible', false);

		const shouldPassDraftReplyComponent =
			isDraftReplyExperimentEligible &&
			expVal('draft_reply_pre-pill_quality_improve_before_ga', 'ExperimentVersion', 'Control') !==
				'Control';

		return {
			...props,
			editorApiRef,
			...(props.isAddComment &&
				(shouldPassMentionComponent || shouldPassDraftReplyComponent) && { contentComponents }),
			...(props.isAddComment &&
				shouldPassMentionComponent && {
					editorMentionsAPI,
					setEditorMentionsAPI,
				}),
		};
	}, [
		editorApiRef,
		props,
		contentComponents,
		editorMentionsAPI,
		setEditorMentionsAPI,
		isMentionExperimentEligible,
		isDraftReplyExperimentEligible,
	]);

	return <IssueEditorInternal runtimeProps={runtimeProps} />;
};

const IssueEditorInternal = ({ runtimeProps }: { runtimeProps: unknown }) => {
	const pressTracing = usePressTracing('issue-editor-load');
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		issueEditorEntrypoint,
		entryPointParams,
	);

	// Tracking the editor loading should start at the point of user interaction, as should loading the entrypoint.
	// Everything in this useEffect should actually be moved out of this component and closer to whatever
	// user interaction triggers the editor to appear. (e.g., pressing 'm' to start a new issue comment)
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		fg('jiv-20205-more-ufo-tracing') && pressTracing();
		entryPointActions.load();
	}, [pressTracing, entryPointActions]);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="lazyIssueEditor"
			packageName="issueViewCommon"
			errorFallback="unmount"
			teamName="team-bento"
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore -  error TS2322: Type are incompatible
			runtimeProps={runtimeProps}
		/>
	);
};

export const LazyIssueEditor = componentWithCondition(
	() => {
		const [experimentConfigJSM] = UNSAFE_noExposureExp('loom_jsm_agent_response_touchpoint');
		return (
			fg('loom_jsm_experiment_controller') &&
			experimentConfigJSM.get('cohort', 'not-enrolled') === 'variation'
		);
	},
	IssueEditorWithLoomEmbedder,
	IssueEditor,
);
